import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, RefreshCw, Search, Webhook } from 'lucide-react';
import { createForgeOrg, discoverRepositories, registerForgeOrgHook, getIdentities, listForgeOrgs, listLabelGroups, listRepositories, mirrorStatus, syncRepository, updateIdentities, updateLabelGroup, updateRepository } from '@mkcore/dev/schema';
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Mono, NativeSelect, PageHeader, Skeleton, Switch, TBody, THead, Table, Td, Textarea, Th, Tr, toast } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { organisationsQuery } from '@/features/crm/queries';
import { projectsQuery } from '@/features/projects/queries';
import { FeedbackSources } from '@/features/dev/pilot';

export const Route = createFileRoute('/_app/dev/settings')({ component: MirrorSettings });

const statusQuery = queryOptions({ queryKey: ['dev', 'mirror', 'status'], queryFn: () => call(mirrorStatus, {}) });
const orgsQuery = queryOptions({ queryKey: ['dev', 'forgeOrg', 'list'], queryFn: () => call(listForgeOrgs, {}) });
const reposQuery = queryOptions({ queryKey: ['dev', 'repo', 'list'], queryFn: () => call(listRepositories, {}), refetchInterval: 5000 });
const groupsQuery = queryOptions({ queryKey: ['dev', 'labelGroup', 'list'], queryFn: () => call(listLabelGroups, {}) });

const phaseTone = { new: 'outline', backfill: 'info', ready: 'success', stopped: 'neutral' } as const;
const statusTone = { ok: 'success', error: 'danger', uncertain: 'warning' } as const;

/**
 * The issue mirror (CO-1272, CO-0196, CO-0024): which forge organisations and repositories are mirrored, their sync
 * state, and what the forge needs (the webhook URL and the two secrets).
 */
function MirrorSettings() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const canManage = useCan('dev:manage');
  const status = useQuery(statusQuery);
  const orgs = useQuery(orgsQuery);
  const repos = useQuery(reposQuery);
  const clients = useInfiniteQuery(organisationsQuery({ limit: 200 }));
  const clientName = new Map((clients.data?.pages.flatMap((p) => p.items) ?? []).map((o) => [o.id, o.name] as const));
  const projects = useInfiniteQuery(projectsQuery({ limit: 200 }));
  const projectList = projects.data?.pages.flatMap((p) => p.items) ?? [];
  const invalidate = [['dev']];
  const discover = useActionMutation(discoverRepositories, { invalidate, success: (r) => t('mirror.discovered', { found: r.found, added: r.added }) });
  const update = useActionMutation(updateRepository, { invalidate });
  const sync = useActionMutation(syncRepository, { invalidate, success: t('mirror.syncStarted') });
  const hook = useActionMutation(registerForgeOrgHook, { invalidate, success: t('mirror.hookRegistered') });
  if (orgs.isError) return <ErrorState title={t('errors.generic')} description={message(orgs.error)} />;
  return (
    <>
      <PageHeader eyebrow={t('nav.dev')} title={t('mirror.title')} description={t('mirror.description')} />
      {status.data && !status.data.configured && (
        <Card className="mb-4 border-warning/40 p-4 text-sm" role="status">
          <p className="font-bold">{t('mirror.notConfigured')}</p>
          <ul className="mt-1 list-disc pl-5 text-fg-2">
            {!status.data.forgeUrl && <li>{t('mirror.needForgeUrl')}</li>}
            {!status.data.syncTokenSet && (
              <li>
                {t('mirror.needSyncToken')}{' '}
                <Link to="/admin/secrets" className="underline">
                  {t('nav.secrets')}
                </Link>
              </li>
            )}
          </ul>
        </Card>
      )}
      {status.data && !status.data.webhookSecretSet && <p className="mb-4 text-sm text-fg-3">{t('mirror.needWebhookSecret')}</p>}
      {canManage && <NewOrg clients={[...clientName.entries()]} />}
      {!orgs.data ? (
        <Skeleton className="h-48" />
      ) : !orgs.data.items.length ? (
        <EmptyState title={t('mirror.empty')} />
      ) : (
        orgs.data.items.map((o) => (
          <Card key={o.id} className="mb-4 grid gap-3 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-bold">{o.login}</h2>
              {o.organisationId && <Badge tone="outline">{clientName.get(o.organisationId) ?? o.organisationId}</Badge>}
              <span className="text-sm text-fg-3">{t('mirror.counts', { mirrored: o.mirrored, total: o.repositories })}</span>
              {canManage && (
                <Button size="sm" variant="secondary" loading={discover.isPending} onClick={() => discover.mutate({ input: { id: o.id } })}>
                  <Search /> {t('mirror.discover')}
                </Button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-fg-3">{t('mirror.webhookUrl')}:</span>
              <Mono className="text-xs">{o.webhookUrl}</Mono>
              <Button
                size="icon"
                variant="ghost"
                aria-label={t('common.copy')}
                onClick={() => void navigator.clipboard.writeText(o.webhookUrl).then(() => toast.success(t('common.copied')))}
              >
                <Copy />
              </Button>
              {o.lastWebhookAt && <span className="text-fg-3">{t('mirror.lastWebhook', { at: formatDateTime(o.lastWebhookAt) })}</span>}
              {o.hookRegisteredAt && <span className="text-fg-3">{t('mirror.hookAt', { at: formatDateTime(o.hookRegisteredAt) })}</span>}
              {canManage && (
                <Button size="sm" variant="secondary" loading={hook.isPending && hook.variables?.input.id === o.id} onClick={() => hook.mutate({ input: { id: o.id } })}>
                  <Webhook /> {o.hookId ? t('mirror.updateHook') : t('mirror.registerHook')}
                </Button>
              )}
            </div>
            <Table>
              <THead>
                <Tr>
                  <Th>{t('mirror.repository')}</Th>
                  <Th>{t('mirror.mirrored')}</Th>
                  <Th>{t('mirror.sync')}</Th>
                  <Th className="hidden lg:table-cell">{t('mirror.project')}</Th>
                  <Th className="hidden md:table-cell">{t('mirror.lastRun')}</Th>
                  <Th className="hidden md:table-cell text-right">{t('mirror.issues')}</Th>
                  {canManage && <Th className="text-right">{t('common.actions')}</Th>}
                </Tr>
              </THead>
              <TBody>
                {(repos.data?.items ?? [])
                  .filter((r) => r.forgeOrgId === o.id)
                  .map((r) => (
                    <Tr key={r.id}>
                      <Td>
                        <a href={r.url} target="_blank" rel="noreferrer" className="font-bold hover:underline">
                          {r.name}
                        </a>
                        {r.description && <div className="text-xs text-fg-3">{r.description}</div>}
                      </Td>
                      <Td>
                        <Switch
                          aria-label={t('mirror.toggle', { name: r.fullName })}
                          checked={r.enabled}
                          disabled={!canManage || update.isPending}
                          onCheckedChange={(v) => update.mutate({ input: { id: r.id, enabled: v }, ifMatch: r.version })}
                        />
                      </Td>
                      <Td>
                        {r.sync ? (
                          <span className="inline-flex flex-wrap gap-1">
                            <Badge tone={phaseTone[r.sync.phase]}>{t(`mirror.phase.${r.sync.phase}`)}</Badge>
                            <Badge tone={statusTone[r.sync.status]}>{t(`mirror.status.${r.sync.status}`)}</Badge>
                            {r.sync.phase === 'backfill' && (
                              <span className="text-xs text-fg-3">{t('mirror.backfillAt', { step: r.sync.backfillStep, page: r.sync.backfillPage })}</span>
                            )}
                          </span>
                        ) : (
                          '—'
                        )}
                        {r.sync?.error && <div className="mt-1 max-w-xs truncate text-xs text-danger">{r.sync.error}</div>}
                      </Td>
                      <Td className="hidden lg:table-cell">
                        <NativeSelect
                          aria-label={t('mirror.projectOf', { name: r.fullName })}
                          value={r.projectId ?? ''}
                          disabled={!canManage || update.isPending}
                          onChange={(e) => update.mutate({ input: { id: r.id, projectId: e.target.value || null }, ifMatch: r.version })}
                        >
                          <option value="">{t('mirror.noProject')}</option>
                          {projectList.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </NativeSelect>
                      </Td>
                      <Td className="hidden md:table-cell tabular text-fg-2">{r.sync?.lastReconcileAt ? formatDateTime(r.sync.lastReconcileAt) : '—'}</Td>
                      <Td className="hidden md:table-cell text-right tabular">{r.sync?.counts.issues ?? '—'}</Td>
                      {canManage && (
                        <Td className="text-right">
                          {r.enabled && (
                            <Button size="sm" variant="ghost" loading={sync.isPending && sync.variables?.input.id === r.id} onClick={() => sync.mutate({ input: { id: r.id, full: false } })}>
                              <RefreshCw /> {t('mirror.syncNow')}
                            </Button>
                          )}
                        </Td>
                      )}
                    </Tr>
                  ))}
              </TBody>
            </Table>
          </Card>
        ))
      )}
      <LabelGroups canManage={canManage} />
      <IdentitySettings canManage={canManage} />
      <FeedbackSources clients={[...clientName]} />
    </>
  );
}

/** Label groups (CO-0394): which scoped groups a board may change; the default allowlist is tier, severity, area, phase and triage. */
function LabelGroups({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation();
  const groups = useQuery(groupsQuery);
  const update = useActionMutation(updateLabelGroup, { invalidate: [['dev', 'labelGroup']] });
  if (!groups.data?.items.length) return null;
  return (
    <Card className="mt-2 p-4">
      <h2 className="mb-1 text-lg font-bold">{t('mirror.labelGroups')}</h2>
      <p className="mb-3 text-sm text-fg-3">{t('mirror.labelGroupsHint')}</p>
      <Table>
        <THead>
          <Tr>
            <Th>{t('mirror.group')}</Th>
            <Th className="text-right">{t('mirror.labelCount')}</Th>
            <Th>{t('mirror.writable')}</Th>
          </Tr>
        </THead>
        <TBody>
          {groups.data.items.map((g) => (
            <Tr key={g.name}>
              <Td>
                <Mono className="text-xs">{g.name}</Mono>
                {g.title !== g.name && <span className="ml-2 text-fg-2">{g.title}</span>}
              </Td>
              <Td className="text-right tabular">{g.labels}</Td>
              <Td>
                <Switch aria-label={t('mirror.writableOf', { name: g.name })} checked={g.writable} disabled={!canManage || update.isPending} onCheckedChange={(v) => update.mutate({ input: { name: g.name, writable: v } })} />
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </Card>
  );
}

/** "Martijn Kerver = martijn, m.kerver@example.com": one person per line, as in fg-dash. */
const aliasesToText = (a: { canonical: string; alts: string[] }[]) => a.map((x) => `${x.canonical} = ${x.alts.join(', ')}`).join('\n');
const textToAliases = (text: string) =>
  text
    .split('\n')
    .map((line) => line.split('='))
    .filter(([canonical]) => canonical?.trim())
    .map(([canonical, alts = '']) => ({ canonical: canonical!.trim(), alts: alts.split(',').map((x) => x.trim()).filter(Boolean) }));

/** Identity aliases and bot patterns for commit activity (CO-0417). */
function IdentitySettings({ canManage }: { canManage: boolean }) {
  const q = useQuery({ queryKey: ['dev', 'identity'], queryFn: () => call(getIdentities, {}) });
  if (!q.data) return null;
  return <IdentityForm key={JSON.stringify(q.data)} initial={q.data} canManage={canManage} />;
}

function IdentityForm({ initial, canManage }: { initial: { aliases: { canonical: string; alts: string[] }[]; bots: string[] }; canManage: boolean }) {
  const { t } = useTranslation();
  const [aliases, setAliases] = useState(aliasesToText(initial.aliases));
  const [bots, setBots] = useState(initial.bots.join(', '));
  const save = useActionMutation(updateIdentities, { invalidate: [['dev', 'identity'], ['dev', 'activity']], success: t('common.saved') });
  return (
    <Card className="mt-4 p-4">
      <h2 className="mb-1 text-lg font-bold">{t('identities.title')}</h2>
      <p className="mb-3 text-sm text-fg-3">{t('identities.hint')}</p>
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ input: { aliases: textToAliases(aliases), bots: bots.split(',').map((x) => x.trim()).filter(Boolean) } });
        }}
      >
        <Field label={t('identities.aliases')}>
          {(p) => <Textarea {...p} rows={4} className="font-mono text-sm" value={aliases} disabled={!canManage} placeholder="Martijn Kerver = martijn, m.kerver@example.com" onChange={(e) => setAliases(e.target.value)} />}
        </Field>
        <Field label={t('identities.bots')}>{(p) => <Input {...p} value={bots} disabled={!canManage} onChange={(e) => setBots(e.target.value)} />}</Field>
        {canManage && (
          <div>
            <Button type="submit" loading={save.isPending}>
              {t('common.save')}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

function NewOrg({ clients }: { clients: (readonly [string, string])[] }) {
  const { t } = useTranslation();
  const [login, setLogin] = useState('');
  const [organisationId, setOrganisationId] = useState('');
  const create = useActionMutation(createForgeOrg, { invalidate: [['dev']], success: t('mirror.orgAdded') });
  return (
    <Card className="mb-4 flex flex-wrap items-end gap-3 p-4">
      <Field label={t('mirror.orgLogin')} className="w-56">
        {(p) => <Input {...p} value={login} placeholder="MKC" onChange={(e) => setLogin(e.target.value)} />}
      </Field>
      <Field label={t('mirror.client')} className="w-56">
        {(p) => (
          <NativeSelect {...p} value={organisationId} onChange={(e) => setOrganisationId(e.target.value)}>
            <option value="">{t('mirror.ownOrg')}</option>
            {clients.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </NativeSelect>
        )}
      </Field>
      <Button loading={create.isPending} disabled={!login.trim()} onClick={() => create.mutate({ input: { login: login.trim(), organisationId: organisationId || null } }, { onSuccess: () => setLogin('') })}>
        {t('mirror.addOrg')}
      </Button>
    </Card>
  );
}
